import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Pencil, Trash2, Plus, X } from "lucide-react";

const authHeaders = () => {
  const token = localStorage.getItem("lsp_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // null or post object
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/admin/login");
  }, [user, loading, nav]);

  const reload = async () => {
    try {
      const [p, e, c] = await Promise.all([
        api.get("/blog?all=true", { headers: authHeaders() }),
        api.get("/enquiries", { headers: authHeaders() }),
        api.get("/contact", { headers: authHeaders() }),
      ]);
      setPosts(p.data); setEnquiries(e.data); setContacts(c.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  if (loading) return <div className="py-32 text-center text-ink/60">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const startNew = () => { setEditing({ title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true }); setShowForm(true); };
  const startEdit = (p) => { setEditing({ ...p, tags: (p.tags || []).join(", ") }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: editing.title,
      excerpt: editing.excerpt,
      content: editing.content,
      cover_image: editing.cover_image || null,
      tags: (editing.tags || "").split(",").map(s => s.trim()).filter(Boolean),
      published: !!editing.published,
    };
    try {
      if (editing.id) {
        await api.put(`/blog/${editing.id}`, payload, { headers: authHeaders() });
        toast.success("Post updated");
      } else {
        await api.post("/blog", payload, { headers: authHeaders() });
        toast.success("Post created");
      }
      setShowForm(false); setEditing(null); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/blog/${id}`, { headers: authHeaders() });
      toast.success("Deleted"); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-ivory min-h-[calc(100vh-88px)]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-clay">Admin</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-forest">Welcome, {user.name}</h1>
          </div>
          <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout-btn" className="btn-pill btn-ghost">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <Tabs defaultValue="blog" className="w-full">
          <TabsList className="bg-kraft/60 border border-dust">
            <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog Posts ({posts.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Messages ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="blog" className="mt-8">
            <div className="flex justify-end mb-5">
              <button onClick={startNew} data-testid="admin-new-post-btn" className="btn-pill btn-forest"><Plus className="w-4 h-4" /> New post</button>
            </div>

            {showForm && editing && (
              <div className="mb-8 p-6 lg:p-8 rounded-xl border border-dust bg-kraft/20">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-serif text-2xl text-forest">{editing.id ? "Edit Post" : "New Post"}</h2>
                  <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-ink/60 hover:text-forest"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={save} className="space-y-4">
                  <div><Label>Title</Label><Input data-testid="post-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required className="bg-white border-dust mt-1" /></div>
                  <div><Label>Excerpt</Label><Textarea data-testid="post-excerpt-input" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} required rows={2} className="bg-white border-dust mt-1" /></div>
                  <div><Label>Content</Label><Textarea data-testid="post-content-input" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} required rows={8} className="bg-white border-dust mt-1" /></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>Cover image URL</Label><Input data-testid="post-cover-input" value={editing.cover_image || ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} placeholder="/images/gallery/lsp-4.jpg" className="bg-white border-dust mt-1" /></div>
                    <div><Label>Tags (comma separated)</Label><Input data-testid="post-tags-input" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className="bg-white border-dust mt-1" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-[14px]">
                    <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="post-published-checkbox" /> Published
                  </label>
                  <div className="flex gap-3">
                    <button type="submit" data-testid="post-save-btn" className="btn-pill btn-forest">{editing.id ? "Save changes" : "Create post"}</button>
                    <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="btn-pill btn-ghost">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-dust">
              <table className="w-full text-left">
                <thead className="bg-kraft/50 text-[12px] tracking-[0.2em] uppercase text-clay">
                  <tr>
                    <th className="p-4">Title</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Created</th>
                    <th className="p-4 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.id} className="border-t border-dust">
                      <td className="p-4 font-serif text-forest">{p.title}</td>
                      <td className="p-4"><span className={`text-[11px] px-2 py-1 rounded-full ${p.published ? "bg-forest text-ivory" : "bg-dust text-ink"}`}>{p.published ? "Published" : "Draft"}</span></td>
                      <td className="p-4 text-[13px] text-ink/70">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button onClick={() => startEdit(p)} data-testid={`post-edit-${p.id}`} className="p-2 rounded-full hover:bg-kraft"><Pencil className="w-4 h-4 text-forest" /></button>
                          <button onClick={() => del(p.id)} data-testid={`post-delete-${p.id}`} className="p-2 rounded-full hover:bg-kraft"><Trash2 className="w-4 h-4 text-terracotta" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="enquiries" className="mt-8">
            <div className="overflow-hidden rounded-xl border border-dust">
              <table className="w-full text-left">
                <thead className="bg-kraft/50 text-[12px] tracking-[0.2em] uppercase text-clay">
                  <tr><th className="p-4">Name</th><th className="p-4">Phone</th><th className="p-4">Product</th><th className="p-4">Message</th><th className="p-4">When</th></tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-ink/60">No enquiries yet.</td></tr>}
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-t border-dust align-top">
                      <td className="p-4 font-serif text-forest">{e.name}</td>
                      <td className="p-4 text-[14px]">{e.phone}<div className="text-[12px] text-ink/60">{e.email}</div></td>
                      <td className="p-4 text-[14px]">{e.product || "—"}</td>
                      <td className="p-4 text-[13px] text-ink/75 max-w-md">{e.message || "—"}</td>
                      <td className="p-4 text-[13px] text-ink/60 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-8">
            <div className="overflow-hidden rounded-xl border border-dust">
              <table className="w-full text-left">
                <thead className="bg-kraft/50 text-[12px] tracking-[0.2em] uppercase text-clay">
                  <tr><th className="p-4">Name</th><th className="p-4">Phone</th><th className="p-4">Message</th><th className="p-4">When</th></tr>
                </thead>
                <tbody>
                  {contacts.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-ink/60">No messages yet.</td></tr>}
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-t border-dust align-top">
                      <td className="p-4 font-serif text-forest">{c.name}</td>
                      <td className="p-4 text-[14px]">{c.phone}<div className="text-[12px] text-ink/60">{c.email}</div></td>
                      <td className="p-4 text-[13px] text-ink/75 max-w-lg">{c.message}</td>
                      <td className="p-4 text-[13px] text-ink/60 whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
